/**
 * Various default ViewFilters and utilities for interacting with a ViewFilter
 * @module
 */
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { PlanView } from "building-types/views/View3D";
import type {
  ViewFilter,
  ViewFilterFloorAreaTypes,
  ViewFilterPlan,
  ViewFilterPlanAnalyticEntities,
  ViewFilterPlanAnnotation,
  ViewFilterProps,
  ViewFilterSiteShapesTypes,
} from "building-types/views/viewFilterTypes";
import { objectMapToSameObjectType } from "common/utils/objectMap";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { internalViewFilter3DDefaults } from "lib/components/studio/renderStageConstants";

export const defaultPlanViewFilterAreas: ViewFilterFloorAreaTypes = {
  floorAreasConditioned: true,
  floorAreasFrontPorch: true,
  floorAreasGarage: true,
  floorAreasCoveredPatio: true,
  floorAreasUncoveredPatio: true,
  floorAreasUnconditioned: true,
  floorAreasRearPorch: true,
};

export const defaultPlanViewFilterSiteShapes: ViewFilterSiteShapesTypes = {
  siteShapesContour: true,
  siteShapesDriveway: true,
  siteShapesHeightOverrides: true,
  siteShapesRoad: true,
  siteShapesSiteFill: true,
  siteShapesSiteSubtract: true,
  siteShapesRetainingWall: true,
  siteShapesLandscape: true,
  siteShapesWalkway: true,
};

export const emptyViewFilter: ViewFilter = {
  labels: {},
  annotation: false,
  annotationAllByBackgroundSheet: undefined,
  baseTrimMolding: false,
  camera: false,
  casework: false,
  caseworkTags: false,
  ceilings: false,
  columns: false,
  cornerBoards: false,
  crossSection: false,
  crownTrimMolding: false,
  interiorWallPlaneTrimMolding: false,
  debug: false,
  debugBuildingOutlineVertices: false,
  debugLevelExtrusions: false,
  doors: false,
  doorSwings: false,
  doorTags: false,
  doorUnits: false,
  doorCasing: false,
  headerFramingTags: false,
  doorTrimAnalyticEntities: false,
  driveways: false,
  electrical: false,
  elevationAnnotation: false,
  elevationNotes: false,
  gradeLine: false,
  exteriorAutoDimensions: false,
  exteriorLightProbes: false,
  faceDiff: false,
  floorAreas: false,
  ...defaultPlanViewFilterAreas, // Set to true since it's enough to set the parent floorAreas key to false.
  floorSystems: false,
  floorSystemTags: false,
  foundationSystems: false,
  floorFraming: false,
  floorFramingLabels: false,
  flooring: false,
  floorKeyMeasures: false,
  floorMaterialAreas: false,
  foundation: false,
  analyticFoundationSlabs: false,
  analyticFoundationFootings: false,
  analyticStemWalls: false,
  analyticFloorStructures: false,
  analyticRailings: false,
  ceilingAnalyticFaces: false,
  slab: false,
  stemWall: false,
  foundationCurb: false,
  foundationFooting: false,
  foundationPiers: false,
  foundationAutoDimensions: false,
  foundationKeyMeasures: false,
  gableEndWallFraming: false,
  grid: false,
  interiorAutoDimensions: false,
  interiorLightProbes: false,
  interiorLights: false,
  interiorTrimAnalyticEntities: false,
  issues: false,
  lut: false,
  measurements: false,
  misalignedWallWarning: false,
  overrides: false,
  perimeter: false,
  porches: false,
  porchKeyMeasures: false,
  procureMeasuresFlashing: false,
  procureMeasuresEaves: false,
  procureMeasuresRidges: false,
  procureMeasuresRidgeVents: false,
  procureMeasuresRoofAreas: false,
  procureMeasuresValleys: false,
  procureMeasuresFacadeTrim: false,
  procureMeasuresInteriorDoors: false,
  procureMeasuresExteriorDoors: false,
  procureMeasuresInteriorWindows: false,
  procureMeasuresExteriorWindows: false,
  procureMeasuresRoofTrim: false,
  procureMeasuresRoomPerimeter: false,
  propAppliances: false,
  propFixtures: false,
  propFurniture: false,
  propFoliage: false,
  propOther: false,
  propBoundingBoxes: false,
  railingPosts: false,
  railings: false,
  referenceGeom: false,
  roofing: false,
  roofKeyMeasures: false,
  roofAnalyticFaces: false,
  roofAnalyticEdges: false,
  roofAnalyticFlashing: false,
  analyticRoofSoffits: false,
  roofs: false,
  roofsDebug: false,
  roofRidgeVent: false,
  roofSideSelection: false,
  roofVolumes: false,
  buildingGraph: false,
  rooms: false,
  roomTags: false,
  roomSalesLabels: false,
  sao: false,
  sceneLights: false,
  sheet: false,
  site: false,
  siteShapes: false,
  ...defaultPlanViewFilterSiteShapes, // Set to true since it's enough to set the parent siteShapes key to false.
  siteLightProbes: false,
  homeSideIndicator: false,
  solarPotential: false,
  stairSystems: false,
  subFlooring: false,
  slabFlooring: false,
  underlays: false,
  roofUnderlays: false,
  wainscoting: false,
  wallSubstrate: false,
  wallExteriorFinish: false,
  wallInteriorFinishAnalyticFaces: false,
  wallInteriorFinishAnalyticFacesLOD2: false,
  wallExteriorFinishAnalyticFaces: false,
  wallExteriorFinishAnalyticEdges: false,
  wallStructuralDomains: false,
  wallFraming: false,
  wallFramingRegions: false,
  walls: false,
  wallCore: false,
  warmup: false,
  windows: false,
  windowShutters: false,
  windowTags: false,
  windowUnits: false,
  windowCasing: false,
  windowTrimAnalyticEntities: false,
  sectionViews: false,
  roofFraming: false,
} as const;

export const defaultPlanViewFilterAnnotation: Pick<ViewFilter, "labels"> & ViewFilterPlanAnnotation = {
  labels: {},
  annotation: true,
  annotationAllByBackgroundSheet: undefined,
};

const defaultViewFilterPropGeometry: ViewFilterProps = {
  propAppliances: true,
  propFixtures: true,
  propFurniture: true,
  propOther: true,
  propFoliage: true,
};

const defaultPlanViewFilterAnalyticEntities: ViewFilterPlanAnalyticEntities = {
  analyticFloorStructures: false,
  analyticFoundationSlabs: false,
  analyticFoundationFootings: false,
  analyticStemWalls: false,
  analyticRailings: false,
  interiorTrimAnalyticEntities: false,
  roofAnalyticFaces: false,
  roofAnalyticEdges: false,
  roofAnalyticFlashing: false,
  analyticRoofSoffits: false,
  wallInteriorFinishAnalyticFacesLOD2: false,
  ceilingAnalyticFaces: false,
};

/**
 * Note that this defines which view filter categories are user controllable in plan views.
 */
export const defaultPlanViewFilter: ViewFilterPlan = {
  ...defaultPlanViewFilterAnnotation,
  ...defaultPlanViewFilterAreas,
  ...defaultPlanViewFilterSiteShapes,
  ...defaultPlanViewFilterAnalyticEntities,
  ...defaultViewFilterPropGeometry,
  casework: true,
  caseworkTags: true,
  ceilings: true,
  columns: true,
  doors: true,
  doorTags: true,
  doorCasing: false,
  headerFramingTags: false,
  electrical: false,
  elevationNotes: false,
  exteriorAutoDimensions: true,
  floorAreas: false,
  floorSystems: false,
  floorFraming: false,
  floorFramingLabels: false,
  slabFlooring: false,
  subFlooring: false,
  floorMaterialAreas: false,
  floorSystemTags: false,
  foundation: false,
  foundationSystems: true,
  foundationAutoDimensions: false,
  grid: true,
  interiorAutoDimensions: true,
  issues: true,
  measurements: true,
  misalignedWallWarning: true,
  overrides: false,
  perimeter: true,
  porches: true,
  railingPosts: true,
  railings: true,
  referenceGeom: true,
  roofs: false,
  rooms: true,
  roomTags: false,
  roomSalesLabels: false,
  sheet: false,
  stairSystems: true,
  siteShapes: true,
  underlays: true,
  roofUnderlays: true,

  // We have some code that renders wainscoting in plans, but viewFilterKeyToViewType disables control in plan view.
  wainscoting: false,

  wallSubstrate: false,
  wallExteriorFinish: false,
  wallStructuralDomains: false,
  walls: true,
  wallCore: true,
  windows: true,
  windowTags: true,
  windowCasing: false,
  sectionViews: true,
  roofFraming: false,
} as const;

export const AnnotationBySheetField: keyof ViewFilter = "annotationAllByBackgroundSheet";
export const LabelsField: keyof ViewFilter = "labels";

export const allViewFilter = objectMapToSameObjectType(defaultPlanViewFilter, (value, filterName) => {
  if (filterName === AnnotationBySheetField || filterName === LabelsField) {
    return value;
  } else {
    return true;
  }
});

/** It doesn't make sense for these view filters to be affected by the all/invert/clearAll shortcuts. */
export const excludeFromViewFilterShortcuts: (keyof ViewFilter)[] = ["crossSection"];

/**
 * @deprecated Prefer using emptyViewFilter instead.
 */
export const blankViewFilter = objectMapToSameObjectType(defaultPlanViewFilter, (_, filterName) => {
  if (filterName === LabelsField) {
    return {};
  } else if (filterName === AnnotationBySheetField) {
    return undefined;
  } else {
    return false;
  }
});

const baseViewFilter: ViewFilter = {
  ...emptyViewFilter,
  issues: true,
  grid: true,
  referenceGeom: true,
};

const baseContentfulFilter: ViewFilter = {
  ...baseViewFilter,
  ...defaultViewFilterPropGeometry,
  porches: true,
  ceilings: true,
  rooms: true,
  doors: true,
  casework: true,
  caseworkTags: true,
  railings: true,
  walls: true,
  wallCore: true,
  stairSystems: true,
  windows: true,
  underlays: true,
  roofUnderlays: true,
  measurements: true,
  floorSystems: false,
  floorFraming: false,
  floorFramingLabels: false,
  floorSystemTags: false,
  foundationSystems: true,
};

export const editorPreviewViewFilter: ViewFilter = {
  ...emptyViewFilter,
  rooms: true,
  foundationSystems: true,
};

/**
 * View filter used when `EditorPreview` is rendering a synthetic `WallPlaneView` for Edit Mode.
 * Shows the full building (elevation-style) from the selected wall plane direction.
 */
export const defaultWallPlaneViewFilter: ViewFilter = {
  ...emptyViewFilter,
  ...internalViewFilter3DDefaults,
  flooring: true,
  floorFraming: true,
  subFlooring: true,
  slabFlooring: true,
  walls: true,
  wallSubstrate: true,
  wallExteriorFinish: true,
  ceilings: true,
  roofs: true,
  roofVolumes: true,
  cornerBoards: true,
  baseTrimMolding: true,
  crownTrimMolding: true,
  interiorWallPlaneTrimMolding: true,
  wainscoting: true,
  windowUnits: true,
  windowShutters: true,
  doorUnits: true,
  doorSwings: true,
  annotation: true,
  slab: true,
  stemWall: true,
  foundationCurb: true,
  foundationFooting: true,
  foundationPiers: true,
  porches: true,
  stairSystems: true,
  railings: true,
  casework: true,
  driveways: true,
  interiorLights: true,
  columns: true,
  site: true,
  elevationAnnotation: true,
  propAppliances: true,
  propFixtures: true,
  propFoliage: true,
  propFurniture: true,
  propOther: true,
};

export const builtInPlanViewTypeToPlanViewViewerFilter: Record<BuiltinPlanViewType, ViewFilter> = {
  [BuiltinPlanViewType.EMPTY]: {
    ...emptyViewFilter,
  },
  [BuiltinPlanViewType.FLOOR]: {
    ...baseContentfulFilter,
    floorSystems: true,
    exteriorAutoDimensions: true,
    interiorAutoDimensions: true,
    doorTags: true,
    windowTags: true,
    annotation: true,
    labels: makeBuiltInLabelTable("addToFloorPlan"),
  },
  [BuiltinPlanViewType.FOUNDATION]: {
    ...baseViewFilter,
    rooms: true,
    foundation: true,
    foundationSystems: true,
    foundationAutoDimensions: true,
    annotation: true,
    labels: makeBuiltInLabelTable("addToFoundationPlan"),
  },
  [BuiltinPlanViewType.ROOF]: {
    ...baseViewFilter,
    roofs: true,
    roofSideSelection: true,
    annotation: true,
    labels: makeBuiltInLabelTable("addToRoofPlan"),
  },
  [BuiltinPlanViewType.ELECTRICAL]: {
    ...baseContentfulFilter,
    electrical: true,
    annotation: true,
    labels: makeBuiltInLabelTable("addToElectricalPlan"),
  },
  [BuiltinPlanViewType.BROCHURE]: {
    ...baseContentfulFilter,
    annotation: true,
    labels: makeBuiltInLabelTable("addToBrochurePlan"),
    roomSalesLabels: true,
  },
  [BuiltinPlanViewType.UTILITY]: {
    ...baseContentfulFilter,
    annotation: true,
    labels: makeBuiltInLabelTable("addToUtilityPlan"),
  },
  [BuiltinPlanViewType.STRUCTURAL]: {
    ...baseContentfulFilter,
    floorSystems: true,
    floorFraming: true,
    floorFramingLabels: true,
    slabFlooring: true,
    subFlooring: true,
    floorSystemTags: true,
    annotation: true,
    labels: makeBuiltInLabelTable("addToStructuralPlan"),
  },
  [BuiltinPlanViewType.FLOOR_MATERIALS]: {
    ...baseContentfulFilter,
    annotation: true,
    labels: makeBuiltInLabelTable("addToFloorMaterialsPlan"),
  },
  [BuiltinPlanViewType.FLOOR_SUMMARY]: {
    ...baseContentfulFilter,
    annotation: true,
    labels: makeBuiltInLabelTable("addToFloorSummaryPlan"),
  },
  [BuiltinPlanViewType.BACKGROUND]: {
    ...baseContentfulFilter,
    annotation: true,
  },
};

export function planViewGetCompleteViewFilter(view: PlanView): ViewFilter {
  return {
    ...builtInPlanViewTypeToPlanViewViewerFilter[view.referenceType],
    ...view.viewFilter,
  };
}

/** Returns true if any view filter Prop categories are enabled */
export function viewFilterPropsEnabled(viewFilter: Partial<ViewFilter>): boolean {
  if (viewFilter.propAppliances || viewFilter.propFixtures || viewFilter.propFurniture || viewFilter.propOther) {
    return true;
  }

  return false;
}
